import type {
  HmrcAuthorizeEnvelope,
  HmrcAuthorizeResponse,
  HmrcTokenEnvelope,
  HmrcRefreshResponse,
  HmrcTokenPayload,
} from '../types'
import { api } from './api'

class HmrcAuthService {
  /**
   * Initiate the HMRC authorisation flow
   */
  async authorize(
    payload?: { companyId?: string }
  ): Promise<HmrcAuthorizeEnvelope> {
    return await api.post<HmrcAuthorizeResponse>(
      '/filling/auth/authorize',
      payload,
      { skipAuthLogout: true } // Don't logout on HMRC auth failures
    )
  }

  /**
   * Exchange HMRC callback params for access tokens.
   * Note: this endpoint is generally invoked by the backend redirect handler,
   * but exposing it here allows SPA polling if required.
   */
  async handleCallback(
    params: Record<string, string>
  ): Promise<HmrcAuthorizeEnvelope> {
    const query = new URLSearchParams(params)
    return await api.get<HmrcAuthorizeResponse>(
      `/filling/auth/callback?${query.toString()}`,
      { skipAuthLogout: true } // Don't logout on HMRC auth failures
    )
  }

  /**
   * Refresh HMRC tokens for a user or company scope
   */
  async refreshTokens(
    payload: HmrcTokenPayload
  ): Promise<HmrcRefreshResponse> {
    return await api.post<HmrcRefreshResponse['response']>(
      '/filling/auth/refresh',
      payload,
      { skipAuthLogout: true } // Don't logout on HMRC auth failures
    )
  }

  /**
   * Fetch stored HMRC refresh token for the current user.
   * This should NOT trigger logout if HMRC auth is missing/invalid.
   */
  async getRefreshToken(): Promise<HmrcTokenEnvelope> {
    return await api.get<HmrcTokenEnvelope['response']>(
      '/filling/auth/token',
      { skipAuthLogout: true } // Don't logout on HMRC auth failures
    )
  }
}

export const hmrcAuthService = new HmrcAuthService()
export default hmrcAuthService
